import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOMES = [
  { value: 'won', label: 'Won', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'follow_up_booked', label: 'Call booked', cls: 'bg-teal-100 text-teal-700' },
  { value: 'no_response', label: 'No response', cls: 'bg-slate-100 text-slate-500' },
  { value: 'not_interested', label: 'Not interested', cls: 'bg-red-100 text-red-600' },
];

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

export const QuickReplyOutcomes = ({ onCount, refreshKey }) => {
  const [data, setData] = useState(null);
  const [tplStats, setTplStats] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/sms/quick-replies`, { params: { days: 30 } })
      .then((r) => { setData(r.data); onCount?.((r.data.sent || []).length); })
      .catch(() => {});
    axios.get(`${API}/sms/template-analytics`, { params: { days: 90 } })
      .then((r) => setTplStats(r.data.templates || [])).catch(() => {});
  }, [onCount]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const setOutcome = async (id, outcome) => {
    try {
      await axios.patch(`${API}/analytics/sms-dispatches/${id}/outcome`, { outcome });
      toast.success(outcome ? 'Outcome saved' : 'Outcome cleared');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save outcome'); }
  };

  const sent = data?.sent || [];
  if (sent.length === 0) return null;
  const { tagged, converted } = data.stats;

  return (
    <div className="mt-3 pt-3 border-t border-amber-200" data-testid="quick-reply-outcomes">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-amber-800 hover:text-amber-950" data-testid="quick-reply-outcomes-toggle">
          Replies sent · last 30 days ({sent.length}) {open ? '▾' : '▸'}
        </button>
        <span className="text-[11px] font-semibold text-amber-700" data-testid="quick-reply-conversion-stat">
          {tagged > 0 ? `${converted} of ${tagged} tagged converted (${Math.round((converted / tagged) * 100)}%)` : 'Tag outcomes to see what converts'}
        </span>
      </div>
      {open && (
        <div className="mt-2 space-y-1.5">
          {tplStats && tplStats.some((t) => t.tagged > 0) && (
            <div className="bg-amber-100/60 border border-amber-200 rounded-lg px-3 py-2" data-testid="template-leaderboard">
              <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wide mb-1">What converts · last 90 days</p>
              {tplStats.slice(0, 5).map((t) => (
                <div key={t.label} className="flex items-center justify-between gap-2 text-xs py-0.5" data-testid={`tpl-stat-${t.label.slice(0, 20)}`}>
                  <span className="text-slate-700 truncate flex-1">“{t.label.length > 50 ? `${t.label.slice(0, 50)}…` : t.label}”</span>
                  <span className="text-slate-500 shrink-0">{t.sent} sent</span>
                  <span className={`font-semibold shrink-0 ${t.booked > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
                    {t.booked} booked{t.tagged > 0 ? ` (${Math.round((t.booked / t.tagged) * 100)}%)` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {sent.slice(0, 10).map((s) => (
            <div key={s.id} className="bg-white border border-amber-100 rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap" data-testid={`sent-reply-row-${s.id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-slate-900"><span className="font-semibold">{s.to_phone}</span> <span className="text-slate-400">· {ago(s.sent_at)}</span></p>
                <p className="text-xs text-slate-600 mt-0.5">“{s.content}”</p>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {OUTCOMES.map((o) => (
                  <button key={o.value}
                    onClick={() => setOutcome(s.id, s.outcome === o.value ? null : o.value)}
                    className={`text-[10px] font-semibold px-2 py-1 rounded-full border ${s.outcome === o.value ? `${o.cls} border-current` : 'bg-white text-slate-400 border-slate-200 hover:border-amber-300 hover:text-amber-700'}`}
                    data-testid={`outcome-${o.value}-${s.id}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
